import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWishlist } from '../contexts/WishlistContext';
import api from '../services/api';
import { FiUser, FiMapPin, FiHeart, FiEdit, FiTrash2, FiPlus, FiX } from 'react-icons/fi';

const Profile = () => {
  const { isAuthenticated } = useAuth();
  const { refreshWishlist } = useWishlist();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Form states
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: ''
  });
  
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    fullName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    landmark: '',
    is_default: false
  });
  
  const [editingAddress, setEditingAddress] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'wishlist' || tab === 'addresses' || tab === 'profile') {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (contentRef.current) {
      const top = contentRef.current.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [profileResponse, wishlistResponse] = await Promise.all([
          api.auth.getProfile(),
          api.products.getWishlist()
        ]);

        if (profileResponse.data.success) {
          setProfile(profileResponse.data.user);
          setProfileForm({
            name: profileResponse.data.user.name || '',
            phone: profileResponse.data.user.phone || ''
          });
        }

        if (wishlistResponse.data.success) {
          setWishlist(wishlistResponse.data.wishlist);
        }
      } catch (err) {
        console.error('Error fetching profile data:', err);
        setError(api.utils.handleError(err));
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [isAuthenticated]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const response = await api.auth.updateProfile(profileForm);
      if (response.data.success) {
        setProfile(response.data.user);
        setEditProfile(false);
        // Update user context
        // You might want to update the AuthContext here
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      alert(api.utils.handleError(err));
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    
    try {
      const response = await api.auth.addAddress(addressForm);
      if (response.data.success) {
        // Refresh profile data
        const profileResponse = await api.auth.getProfile();
        if (profileResponse.data.success) {
          setProfile(profileResponse.data.user);
        }
        setShowAddAddress(false);
        setAddressForm({
          fullName: '',
          address: '',
          city: '',
          state: '',
          pincode: '',
          phone: '',
          landmark: '',
          is_default: false
        });
      }
    } catch (err) {
      console.error('Error adding address:', err);
      alert(api.utils.handleError(err));
    }
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    
    try {
      const response = await api.auth.updateAddress(editingAddress.id, addressForm);
      if (response.data.success) {
        // Refresh profile data
        const profileResponse = await api.auth.getProfile();
        if (profileResponse.data.success) {
          setProfile(profileResponse.data.user);
        }
        setEditingAddress(null);
        setAddressForm({
          fullName: '',
          address: '',
          city: '',
          state: '',
          pincode: '',
          phone: '',
          landmark: '',
          is_default: false
        });
      }
    } catch (err) {
      console.error('Error updating address:', err);
      alert(api.utils.handleError(err));
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      const response = await api.auth.deleteAddress(addressId);
      if (response.data.success) {
        // Refresh profile data
        const profileResponse = await api.auth.getProfile();
        if (profileResponse.data.success) {
          setProfile(profileResponse.data.user);
        }
      }
    } catch (err) {
      console.error('Error deleting address:', err);
      alert(api.utils.handleError(err));
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    try {
      const response = await api.products.removeFromWishlist(productId);
      if (response.data.success) {
        setWishlist(prev => prev.filter(item => item.id !== productId));
        refreshWishlist();
      }
    } catch (err) {
      console.error('Error removing from wishlist:', err);
      alert(api.utils.handleError(err));
    }
  };

  const startEditAddress = (address) => {
    setEditingAddress(address);
    setAddressForm({
      fullName: address.fullName || '',
      address: address.address || '',
      city: address.city || '',
      state: address.state || '',
      pincode: address.pincode || '',
      phone: address.phone || '',
      landmark: address.landmark || '',
      is_default: address.is_default || false
    });
  };

  const cancelEditAddress = () => {
    setEditingAddress(null);
    setAddressForm({
      fullName: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      phone: '',
      landmark: '',
      is_default: false
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Please login to view your profile</h2>
          <p className="text-gray-600">You need to be logged in to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Error</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">My Profile</h1>
        <p className="text-gray-600">Manage your account information and preferences</p>
      </div>

      {/* Mobile Tab Navigation */}
      <div className="lg:hidden mb-6">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => handleTabChange('profile')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'profile'
                ? 'bg-white text-black shadow-sm'
                : 'text-gray-600 hover:text-black'
            }`}
          >
            Profile
          </button>
          <button
            onClick={() => handleTabChange('addresses')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'addresses'
                ? 'bg-white text-black shadow-sm'
                : 'text-gray-600 hover:text-black'
            }`}
          >
            Addresses
          </button>
          <button
            onClick={() => handleTabChange('wishlist')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'wishlist'
                ? 'bg-white text-black shadow-sm'
                : 'text-gray-600 hover:text-black'
            }`}
          >
            Wishlist
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <div className="card sticky top-24">
            <div className="p-6">
              <nav className="space-y-2">
                <button
                  onClick={() => handleTabChange('profile')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'profile'
                      ? 'bg-black text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FiUser className="inline mr-2" />
                  Profile
                </button>
                <button
                  onClick={() => handleTabChange('addresses')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'addresses'
                      ? 'bg-black text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FiMapPin className="inline mr-2" />
                  Addresses
                </button>
                <button
                  onClick={() => handleTabChange('wishlist')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'wishlist'
                      ? 'bg-black text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FiHeart className="inline mr-2" />
                  Wishlist ({wishlist.length})
                </button>
              </nav>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3" ref={contentRef}>
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div>
              <div className="card">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold flex items-center">
                      <FiUser className="mr-2" />
                      Personal Information
                    </h2>
                    {!editProfile && (
                      <button
                        onClick={() => setEditProfile(true)}
                        className="btn-secondary text-sm"
                      >
                        <FiEdit className="mr-2" />
                        Edit
                      </button>
                    )}
                  </div>

                  {editProfile ? (
                    <form onSubmit={handleProfileUpdate} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Full Name</label>
                        <input
                          type="text"
                          value={profileForm.name}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                          className="input-field"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Email</label>
                        <input
                          type="email"
                          value={profile?.email || ''}
                          className="input-field bg-gray-50"
                          disabled
                        />
                        <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Phone Number</label>
                        <input
                          type="tel"
                          value={profileForm.phone}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="input-field"
                          required
                        />
                      </div>
                      
                      <div className="flex space-x-4">
                        <button type="submit" className="btn-primary">
                          Save Changes
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditProfile(false)}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600">Full Name</label>
                        <p className="text-lg">{profile?.name || 'Not provided'}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-600">Email</label>
                        <p className="text-lg">{profile?.email || 'Not provided'}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-600">Phone Number</label>
                        <p className="text-lg">{profile?.phone || 'Not provided'}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-600">Member Since</label>
                        <p className="text-lg">
                          {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'Unknown'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Addresses Tab */}
          {activeTab === 'addresses' && (
            <div>
              <div className="card">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold flex items-center">
                      <FiMapPin className="mr-2" />
                      My Addresses
                    </h2>
                    <button
                      onClick={() => setShowAddAddress(true)}
                      className="btn-primary text-sm"
                    >
                      <FiPlus className="mr-2" />
                      Add Address
                    </button>
                  </div>

                  {/* Add/Edit Address Form */}
                  {(showAddAddress || editingAddress) && (
                    <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                      <h3 className="text-lg font-medium mb-4">
                        {editingAddress ? 'Edit Address' : 'Add New Address'}
                      </h3>
                      <form onSubmit={editingAddress ? handleUpdateAddress : handleAddAddress} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Full Name *</label>
                            <input
                              type="text"
                              value={addressForm.fullName}
                              onChange={(e) => setAddressForm(prev => ({ ...prev, fullName: e.target.value }))}
                              className="input-field"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">Phone *</label>
                            <input
                              type="tel"
                              value={addressForm.phone}
                              onChange={(e) => setAddressForm(prev => ({ ...prev, phone: e.target.value }))}
                              className="input-field"
                              required
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Address *</label>
                          <textarea
                            value={addressForm.address}
                            onChange={(e) => setAddressForm(prev => ({ ...prev, address: e.target.value }))}
                            className="input-field"
                            rows="3"
                            required
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">City *</label>
                            <input
                              type="text"
                              value={addressForm.city}
                              onChange={(e) => setAddressForm(prev => ({ ...prev, city: e.target.value }))}
                              className="input-field"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">State *</label>
                            <input
                              type="text"
                              value={addressForm.state}
                              onChange={(e) => setAddressForm(prev => ({ ...prev, state: e.target.value }))}
                              className="input-field"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">Pincode *</label>
                            <input
                              type="text"
                              value={addressForm.pincode}
                              onChange={(e) => setAddressForm(prev => ({ ...prev, pincode: e.target.value }))}
                              className="input-field"
                              required
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Landmark (Optional)</label>
                          <input
                            type="text"
                            value={addressForm.landmark}
                            onChange={(e) => setAddressForm(prev => ({ ...prev, landmark: e.target.value }))}
                            className="input-field"
                          />
                        </div>
                        
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="is_default"
                            checked={addressForm.is_default}
                            onChange={(e) => setAddressForm(prev => ({ ...prev, is_default: e.target.checked }))}
                            className="mr-2"
                          />
                          <label htmlFor="is_default" className="text-sm">Set as default address</label>
                        </div>
                        
                        <div className="flex space-x-4">
                          <button type="submit" className="btn-primary">
                            {editingAddress ? 'Update Address' : 'Add Address'}
                          </button>
                          <button
                            type="button"
                            onClick={editingAddress ? cancelEditAddress : () => setShowAddAddress(false)}
                            className="btn-secondary"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Addresses List */}
                  <div className="space-y-4">
                    {profile?.addresses && profile.addresses.length > 0 ? (
                      profile.addresses.map((address) => (
                        <div key={address.id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h4 className="font-medium">{address.fullName}</h4>
                                {address.is_default && (
                                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                    Default
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-600 mb-1">{address.address}</p>
                              <p className="text-gray-600 mb-1">
                                {address.city}, {address.state} {address.pincode}
                              </p>
                              {address.landmark && (
                                <p className="text-gray-600 mb-1">Landmark: {address.landmark}</p>
                              )}
                              <p className="text-gray-600">Phone: {address.phone}</p>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => startEditAddress(address)}
                                className="p-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg"
                                title="Edit Address"
                              >
                                <FiEdit size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteAddress(address.id)}
                                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg"
                                title="Delete Address"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <FiMapPin className="mx-auto text-gray-400 mb-4" size={48} />
                        <h3 className="text-lg font-medium mb-2">No addresses yet</h3>
                        <p className="text-gray-600 mb-4">Add your first address to get started</p>
                        <button
                          onClick={() => setShowAddAddress(true)}
                          className="btn-primary"
                        >
                          <FiPlus className="mr-2" />
                          Add Address
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Wishlist Tab */}
          {activeTab === 'wishlist' && (
            <div>
              <div className="card">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold flex items-center">
                      <FiHeart className="mr-2" />
                      My Wishlist ({wishlist.length})
                    </h2>
                  </div>

                  {wishlist.length > 0 ? (
                    <div className="product-grid">
                      {wishlist.map((product) => (
                        <div key={product.id} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                          <div className="relative aspect-[4/5] overflow-hidden">
                            <img
                              src={api.utils.getImageUrl(product.images?.[0], { width: 600, height: 750, fit: 'cover' })}
                              alt={product.name}
                              className="w-full h-full object-cover object-center"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=300&h=300&fit=crop';
                              }}
                            />
                            <button
                              onClick={() => handleRemoveFromWishlist(product.id)}
                              className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-red-50 text-red-600 hover:text-red-800 transition-colors"
                              title="Remove from wishlist"
                            >
                              <FiX size={16} />
                            </button>
                          </div>
                          <div className="product-card-info">
                            <h3 className="font-medium mb-2 line-clamp-2">{product.name}</h3>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-lg font-bold">₹{product.price?.toLocaleString()}</span>
                              {product.original_price && product.original_price > product.price && (
                                <span className="text-sm text-gray-500 line-through">
                                  ₹{product.original_price.toLocaleString()}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 mb-3">
                              {product.collection_name && (
                                <p className="mb-1">{product.collection_name}</p>
                              )}
                              {product.totalStock > 0 ? (
                                <p className="text-green-600">In Stock</p>
                              ) : (
                                <p className="text-red-600">Out of Stock</p>
                              )}
                            </div>
                            <button
                              onClick={() => window.location.href = `/products/${product.id}`}
                              className="w-full btn-primary text-sm"
                            >
                              View Product
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FiHeart className="mx-auto text-gray-400 mb-4" size={48} />
                      <h3 className="text-lg font-medium mb-2">Your wishlist is empty</h3>
                      <p className="text-gray-600 mb-4">Start adding products you love to your wishlist</p>
                      <button
                        onClick={() => window.location.href = '/products'}
                        className="btn-primary"
                      >
                        Browse Products
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
